<template>
	<div ref="dropdownRef" class="bg-white rounded-2.5xl shadow-lg p-4 rem:w-[324px] max-w-full">
		<h3 class="typo-h4 text-heading-primary mb-4">
			Vybraný seznam
		</h3>

		<div v-if="status === 'pending'">
			<div class="skeleton-gradient h-[3.125rem] w-full mb-3 rounded-full" />
			<div class="skeleton-gradient h-4 w-1/2 mb-2 mt-3 rounded-full" />
			<div v-for="index in 10" :key="index" class="skeleton-gradient h-5 w-full mb-2 rounded-lg" />
			<div class="skeleton-gradient h-[3.125rem] w-full mt-4 rounded-full" />
		</div>

		<template v-else>
			<ShoppingListMenuDialogSelectorDropdown
				v-if="isAuthenticated"
				class="mb-4"
				:list="shoppingLists?.Items"
				:active-id="activeShoppingList"
				@confirm="changeActiveShoppingList"
			/>

			<div>
				<div v-if="isListItemsLoading" class="overflow-y-auto max-h-[30vh] md:max-h-[40vh] mb-4" aria-busy="true">
					<div v-for="index in 6" :key="index" class="skeleton-gradient h-12 w-full mb-2 rounded-lg" />
				</div>
				<div v-else-if="shoppingListItems?.length" class="overflow-y-auto max-h-[30vh] md:max-h-[40vh] mb-4">
					<ShoppingListMenuDialogItems
						:items="shoppingListItems"
						:pending-states="pendingStates"
						@delete-item="deleteItem"
						@update-amount="updateAmount"
						@update-text="updateTextItem"
						@close-dropdown="close"
					/>
				</div>
				<div v-else class="bg-background-secondary rounded-lg p-2 text-center mb-4">
					<h5 class="typo-h4 mb-2">
						Prázdný nákupní seznam
					</h5>
					<p class="typo-body-caption mb-2">
						Aktuálně nemáte v&nbsp;nákupním seznamu žádné položky
					</p>
					<div class="flex justify-center">
						<AbsoluteInternalLink
							:to="branchSpecificPath({name: 'hypermarket-akcni-nabidka-category'})"
							class="btn btn-primary btn-xs justify-center"
							@click="close"
						>
							Akční nabídka
						</AbsoluteInternalLink>
					</div>
				</div>

				<div v-if="!isAuthenticated" class="rounded-lg p-2 bg-background-secondary mb-4">
					<h5 class="font-normal typo-body-caption text-heading-primary mb-2">
						Registrujte se a naleznete váš nákupní seznam i ve skenerech Scan&Go a mobilní aplikaci Můj Globus.
					</h5>
					<button
						type="button"
						class="btn btn-secondary btn-xs ml-auto flex items-center gap-2"
						@click="handleLogin"
					>
						<NuxtIcon name="user" aria-hidden="true" filled />
						Přihlásit se
					</button>
				</div>
				<div v-else-if="!isInfoBoxDismissed" class="rounded-lg p-2 bg-background-secondary mb-4">
					<h5 class="font-normal typo-body-caption text-heading-primary mb-2">
						<template v-if="isFamilyShoppingList">
							Svůj nákupní seznam najdete také ve skenerech Scan&GO a mobilní aplikaci Můj Globus.
						</template>
						<template v-else>
							Svůj nákupní seznam najdete také v mobilní aplikaci Můj Globus.
						</template>
					</h5>
					<button
						type="button"
						class="btn btn-secondary btn-xs ml-auto hover:shadow-sm active:shadow-inner flex items-center gap-2"
						@click="dismissInfoBox"
					>
						<NuxtIcon name="custom/arrow-right" aria-hidden="true" filled />
						Rozumím
					</button>
				</div>

				<ul
					v-if="isListItemsLoading"
					class="font-bold typo-body-s gap-3 pb-4 mb-4 border-b border-b-border-secondary space-y-2 font-secondary"
					aria-hidden="true"
				>
					<li class="skeleton-gradient h-5 w-full rounded-lg" />
					<li class="skeleton-gradient h-5 w-2/3 rounded-lg" />
				</ul>
				<ul
					v-else-if="shoppingListItems?.length"
					class="font-bold typo-body-s gap-3 pb-4 mb-4 border-b border-b-border-secondary space-y-2 font-secondary"
				>
					<li class="flex justify-between text-body-secondary" aria-live="polite">
						<span>Předběžná cena za nákup</span>
						<span class="rem:min-w-[64px] text-right">{{ formatPrice(totalPrice) }}</span>
					</li>
					<li
						v-if="savedAmount > 0"
						class="flex justify-between text-price-sale text-base"
						aria-live="polite"
					>
						<span>Za nákup celkem ušetříte:</span>
						<span class="rem:min-w-[64px] text-right">{{ formatPrice(-savedAmount) }}</span>
					</li>
				</ul>

				<AbsoluteInternalLink
					v-if="activeShoppingList"
					:to="isAuthenticated
						? {name: 'nakupni-seznamy-id', params: {id: activeShoppingList}}
						: {name: 'nakupni-seznamy'}"
					class="btn btn-primary btn-lg w-full flex justify-center mt-4"
					@click="close"
				>
					Detail seznamu
				</AbsoluteInternalLink>
				<div class="sr-only" aria-live="polite">
					{{ ariaStatusMessage }}
				</div>
			</div>
		</template>
	</div>
</template>

<script setup lang="ts">
	import {storeToRefs} from 'pinia';
	import {useFocusTrap} from '@vueuse/integrations/useFocusTrap';
	import {useShoppingListsStore} from '~/stores/shoppingLists';
	import type {GsoaShoppingListGetShoppingListresponseWithProductDetail} from '~/utils/shoppingList';
	import useShoppingListSummary from '~/composables/useShoppingListSummary';
	import type {
		Gsoa_ShoppingList_GetItemsCollection as GsoaShoppingListGetItemsCollection,
		Gsoa_ShoppingList_UpdateItem as GsoaShoppingListUpdateItem,
		Gsoa_ShoppingList_GetItems_response as ItemsResponse,
	} from '~/openapi';
	import {shoppingListsBusKey} from '~/buses/shoppingLists';
	import {UserState} from '~/stores/crmStateStore';
	import {formatPrice} from '~/utils';

	interface Props {
		visible: boolean
	}

	interface ShoppingListItems {
		id: number
		products: ItemsResponse
	}

	interface Emits {
		(e: 'update:visible', visible: boolean): void
	}

	export type PendingStates = Record<'update' | 'delete', Record<number, ComputedRef<boolean>>>;

	const {isAuthenticated} = useAuth();

	const shoppingListsStore = useShoppingListsStore();
	const {selectedShoppingList, shoppingLists, shoppingListsItems} = storeToRefs(shoppingListsStore);

	const pendingStates = reactive<PendingStates>({update: {}, delete: {}});
	const {pending: isListItemsLoading, handler: withListItemsLoading} = usePending();

	const ariaStatusMessage = useAriaStatusMessage('');

	const activeShoppingList = selectedShoppingList;
	const isFamilyShoppingList = computed(() => (
		shoppingLists.value?.Items?.find((item) => item.ID === activeShoppingList.value)?.IsActive === true
	));
	const shoppingListItems = computed(() => shoppingListsItems.value?.find((item: ShoppingListItems) => item.id === activeShoppingList.value)?.products.Items);
	const typedShoppingListItems = computed(() => (
		shoppingListItems.value ?? []
	) as GsoaShoppingListGetShoppingListresponseWithProductDetail[]);
	const {totalPrice, savedAmount} = useShoppingListSummary(typedShoppingListItems);

	const {status} = await useLazyAsyncData(`shopping-list-items-${activeShoppingList.value}`, async () => {
		try {
			const operations = [
				shoppingListsStore.fetchShoppingLists(),
				activeShoppingList.value !== null
					&& shoppingListsStore.fetchShoppingListItems(String(activeShoppingList.value)),
			].filter(Boolean);

			await Promise.all(operations);
			return true;
		} catch (e: any) {
			throw createError({
				message: 'Failed to fetch shopping-list-items',
				cause: e,
			});
		}
	});

	const props = defineProps<Props>();
	const emit = defineEmits<Emits>();

	const {isDismissed: isInfoBoxDismissed, dismiss: dismissInfoBox} = useShoppingListInfoBox();

	const dropdown = useTemplateRef('dropdownRef');

	const vModelVisible = useVModel(props, 'visible', emit);
	const {activate: activateFocusTrap, deactivate: deactivateFocusTrap} = useFocusTrap(dropdown, {allowOutsideClick: true});

	const branchSpecificPath = useBranchSpecificPath();

	if (import.meta.client) {
		const shoppingListsBus = useEventBus(shoppingListsBusKey);

		const unsubscribeEventBus = shoppingListsBus.on((e: any) => {
			switch (e.type) {
				case 'add':
					ariaStatusMessage.value = 'Produkt byl přidán do nákupního seznamu.';
					break;

				case 'remove':
					ariaStatusMessage.value = 'Produkt byl odebrán z nákupního seznamu.';
					break;
			}
		});

		onUnmounted(() => {
			unsubscribeEventBus();
		});
	}

	const sendNewAmount = useDebounceFn(async (listId: string, itemId: number, requestBody: GsoaShoppingListUpdateItem) => {
		const {pending, handler} = usePending();
		pendingStates.update[itemId] = pending;
		await handler(() => shoppingListsStore.setAmountShoppingListItem(listId, itemId, requestBody));
	}, 500);

	const {redirectToCrm} = useCrmRedirect();

	function close() {
		vModelVisible.value = false;
	}

	function handleLogin() {
		redirectToCrm(UserState.USER_MENU_ANONYMOUS_SLIDEOUT_LOGIN);
	}

	async function fetchItems(id?: string) {
		await shoppingListsStore.fetchShoppingListItems(id);
	}

	async function changeActiveShoppingList(id: number) {
		if (id === selectedShoppingList.value) {
			return;
		}

		const list = shoppingLists.value?.Items?.find((item) => item.ID === id);
		if (!list) {
			return;
		}

		await withListItemsLoading(async () => {
			await shoppingListsStore.setSelectedShoppingList(String(id), list.Name);
			await fetchItems(String(id));
		});
	}

	async function deleteItem(item: GsoaShoppingListGetItemsCollection) {
		const {pending, handler} = usePending();
		pendingStates.delete[item.ID] = pending;
		await handler(() => shoppingListsStore.deleteShoppingListItem(String(activeShoppingList.value), String(item.ID)));
	}

	async function updateTextItem(item: GsoaShoppingListGetItemsCollection, text: string) {
		const trimmed = text.trim();
		if (!trimmed || trimmed === item.Text) {
			return;
		}

		const {pending, handler} = usePending();
		pendingStates.update[item.ID] = pending;
		item.Text = trimmed;
		await handler(() => shoppingListsStore.updateShoppingListItemText(
			String(activeShoppingList.value),
			item.ID,
			trimmed,
		));
	}

	function updateAmount(item: GsoaShoppingListGetItemsCollection, amount: number) {
		item.Amount = amount;
		const requestBody = ref<GsoaShoppingListUpdateItem>({
			Text: item.Text,
			Amount: amount,
		});
		sendNewAmount(String(activeShoppingList.value), item.ID, requestBody.value);
	}

	watch(status, async (newValue) => {
		await nextTick();
		if (newValue === 'success') {
			activateFocusTrap();
		} else {
			deactivateFocusTrap();
		}
	});

	watch(() => props.visible, async (newValue) => {
		if (newValue && activeShoppingList.value !== null) {
			await withListItemsLoading(() => fetchItems(String(activeShoppingList.value)));
		}
	}, {immediate: true});
</script>
